import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, createUserWithEmailAndPassword, db, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import PublicMarketingLayout from '../components/layout/PublicMarketingLayout';
import '../styles/enrolment.css';
import { getServiceById } from '../data/serviceCatalogue';
import { createConsultationRequest } from '../services/consultationRequestService';
import {
  buildBookPageConsentEvents,
  buildConsentSnapshotMap,
  recordConsentEvents,
} from '../services/consentService';
import { readFirstTouchForSignup } from '../services/acquisitionService';
import type { FirstTouchPayload } from '../types/acquisition';
import {
  ONBOARDING_CONSENT_VERSION,
  type ConsentCollectionSnapshot,
} from '../types/consent';
import {
  clearEnrollmentDraft,
  readEnrollmentDraft,
  writeEnrollmentDraft,
  type EnrollmentDraftForm,
} from '../services/enrollmentDraftService';
import EligibilityScreening from '../components/enrollment/EligibilityScreening';
import {
  SCREENING_QUESTIONS,
  recordEligibilityScreening,
  type EligibilityAnswers,
  type EligibilityFlag,
} from '../services/eligibilityService';

type CheckoutPackageId = 'assessment_only' | 'core_membership';
type EnrollmentOptionId = 'membership_monthly' | 'membership_annual';

interface EnrollmentOption {
  id: EnrollmentOptionId;
  label: string;
  family: 'membership';
  checkoutPackageId: CheckoutPackageId;
  /** Headline price shown on the right of the card. */
  priceSummary: string;
  /** Italic one-line description of the cadence. */
  cadence: string;
  /** The initiation-fee line shared by both options. */
  initiationNote: string;
  /** Small note shown under the submit button. */
  detailNote?: string;
  /** Optional ribbon, e.g. "Most chosen". */
  badge?: string;
}

interface FormData extends EnrollmentDraftForm {}

const ENROLLMENT_PATH = '/enrollment';
const ENROLLMENT_THANK_YOU_PATH = '/enrollment/thank-you';

// One membership — the Melio membership. (A second "Plus" tier was retired;
// `plus_membership` now only ever arrives as a legacy inbound link and is
// normalised to the monthly option below.) The intro assessment (£550) is the
// one-time initiation fee. Members then choose how to pay the membership:
// monthly (on top) or for the full year in one payment.
//
// NOTE: both options currently route through the live monthly `core_membership`
// Stripe price. A dedicated annual Stripe price is still required before the
// "pay for the year" option bills as a single annual charge — that is a Stripe
// change and needs founder sign-off (see agent_orchestration/AGENTS.md).
const INITIATION_NOTE = 'Plus a one-time £550 intro assessment — your initiation.';

const ENROLLMENT_OPTIONS: EnrollmentOption[] = [
  {
    id: 'membership_monthly',
    label: 'Pay monthly',
    family: 'membership',
    checkoutPackageId: 'core_membership',
    priceSummary: '£240 / month',
    cadence: 'Spread across the year on a 12-month commitment.',
    initiationNote: INITIATION_NOTE,
    badge: 'Most chosen',
    detailNote:
      'Billed monthly by Stripe on a 12-month commitment. Your one-time intro assessment is taken at the start.',
  },
  {
    id: 'membership_annual',
    label: 'Pay for the year',
    family: 'membership',
    checkoutPackageId: 'core_membership',
    priceSummary: '£2,880 / year',
    cadence: 'Twelve months of membership, settled in one payment.',
    initiationNote: INITIATION_NOTE,
    detailNote:
      'Annual settlement. While our annual price is being finalised on Stripe, this is set up as a 12-month plan and your intro assessment is taken at the start.',
  },
];

const OPTION_LOOKUP = new Map(ENROLLMENT_OPTIONS.map((option) => [option.id, option]));

function splitDisplayName(displayName?: string | null) {
  const trimmed = String(displayName || '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const [firstName, ...rest] = trimmed.split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
}

function normalizePackageParam(value: string | null): EnrollmentOptionId | null {
  if (!value) return null;
  if (value === 'membership_annual' || value === 'annual') return 'membership_annual';
  if (
    value === 'membership_monthly'
    || value === 'monthly'
    || value === 'core_membership'
    || value === 'plus_membership' // legacy link from the retired Plus tier
  ) {
    return 'membership_monthly';
  }
  return null;
}

// Watercolour vignette that bridges two form sections, drifting to one side.
const EnrollVignette: React.FC<{ src: string; align: 'left' | 'right' }> = ({ src, align }) => (
  <div className={`gh-vignette gh-vignette--${align}`} aria-hidden="true">
    <img src={`${process.env.PUBLIC_URL}/media/vignettes/${src}`} alt="" />
  </div>
);

const Checkmark: React.FC = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M2 6.5 L 5 9 L 10 3"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EnrollmentPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedOptionId = normalizePackageParam(searchParams.get('package'));

  // This is the public apply-and-pay funnel: it renders in marketing chrome
  // with no way back into the app, and its submit path calls
  // createUserWithEmailAndPassword. An already-signed-in member must never be
  // able to reach it — several in-app links used to point here, so the guard
  // lives at the destination rather than being maintained at each link.
  // `?renew=1` is the deliberate escape hatch for a genuine re-enrolment.
  const isRenewal = searchParams.get('renew') === '1';
  const redirectSignedInMember = Boolean(currentUser) && !isRenewal;
  const formRef = useRef<HTMLDivElement | null>(null);

  const [selectedOptionId, setSelectedOptionId] = useState<EnrollmentOptionId | null>(
    requestedOptionId,
  );

  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [consents, setConsents] = useState<ConsentCollectionSnapshot>({
    terms: false,
    privacy: false,
    healthDataProcessing: false,
    geneticsContext: false,
    geneticDataRetention: false,
  });

  // Inclusion and Exclusion Criteria §5, §6 — asked before payment.
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [eligibilityAnswers, setEligibilityAnswers] = useState<Partial<EligibilityAnswers>>({});
  const [eligibilityBlockers, setEligibilityBlockers] = useState<EligibilityFlag[]>([]);
  const [eligibilityFlags, setEligibilityFlags] = useState<EligibilityFlag[]>([]);

  useEffect(() => {
    if (requestedOptionId) setSelectedOptionId(requestedOptionId);
  }, [requestedOptionId]);

  const initialNameParts = splitDisplayName(currentUser?.displayName);
  const savedDraft = readEnrollmentDraft();
  const [form, setForm] = useState<FormData>(() => ({
    firstName: savedDraft?.form.firstName || initialNameParts.firstName,
    lastName: savedDraft?.form.lastName || initialNameParts.lastName,
    email: savedDraft?.form.email || currentUser?.email || '',
    phone: savedDraft?.form.phone || '',
    addressLine1: savedDraft?.form.addressLine1 || '',
    addressLine2: savedDraft?.form.addressLine2 || '',
    city: savedDraft?.form.city || '',
    postcode: savedDraft?.form.postcode || '',
    message: savedDraft?.form.message || '',
  }));

  useEffect(() => {
    if (savedDraft?.selectedOptionId && !requestedOptionId) {
      const restored = normalizePackageParam(savedDraft.selectedOptionId);
      if (restored) setSelectedOptionId(restored);
    }
    if (savedDraft?.consents) {
      setConsents((prev) => ({ ...prev, ...savedDraft.consents }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    writeEnrollmentDraft({
      form,
      selectedOptionId,
      consents,
    });
  }, [form, selectedOptionId, consents]);

  useEffect(() => {
    if (!currentUser) return;
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    if (!firstName && !lastName && !form.email.trim()) return;

    const timer = window.setTimeout(() => {
      void setDoc(
        doc(db, 'users', currentUser.uid),
        {
          uid: currentUser.uid,
          email: form.email.trim() || currentUser.email,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          displayName: [firstName, lastName].filter(Boolean).join(' ') || currentUser.displayName,
          phone: form.phone.trim() || undefined,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    }, 800);

    return () => window.clearTimeout(timer);
  }, [currentUser, form.firstName, form.lastName, form.email, form.phone]);

  useEffect(() => {
    if (!currentUser) return;
    void getDoc(doc(db, 'users', currentUser.uid)).then((snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as Record<string, string | undefined>;
      setForm((prev) => ({
        ...prev,
        firstName: prev.firstName || data.firstName || splitDisplayName(currentUser.displayName).firstName,
        lastName: prev.lastName || data.lastName || splitDisplayName(currentUser.displayName).lastName,
        email: prev.email || data.email || currentUser.email || '',
        phone: prev.phone || data.phone || '',
        addressLine1: prev.addressLine1 || data.addressLine1 || '',
        addressLine2: prev.addressLine2 || data.addressLine2 || '',
        city: prev.city || data.city || '',
        postcode: prev.postcode || data.postcode || '',
      }));
    });
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    setForm((prev) => ({
      ...prev,
      firstName: prev.firstName || splitDisplayName(currentUser.displayName).firstName,
      lastName: prev.lastName || splitDisplayName(currentUser.displayName).lastName,
      email: currentUser.email || prev.email,
    }));
  }, [currentUser]);

  const selectedOption = useMemo(
    () => (selectedOptionId ? OPTION_LOOKUP.get(selectedOptionId) ?? null : null),
    [selectedOptionId],
  );

  const checkoutPackageId = selectedOption?.checkoutPackageId ?? 'assessment_only';
  const checkoutProduct = getServiceById(checkoutPackageId);
  const displayName = [form.firstName.trim(), form.lastName.trim()]
    .filter(Boolean)
    .join(' ');

  const requiredConsentsAccepted =
    consents.terms
    && consents.privacy
    && consents.healthDataProcessing
    && consents.geneticsContext;

  /**
   * Eligibility — Inclusion and Exclusion Criteria §5, §6.
   *
   * Every question answered, and a date of birth, before the pay button is live.
   * §5.1(6) requires the applicant to be told the Service is not urgent care,
   * does not prescribe and does not replace their GP "before paying"; §6.3 says an
   * applicant with red flags "is not enrolled until the acute problem is
   * resolved". Screening after payment would get both backwards.
   *
   * The screen holds enrolment on a disclosure rather than declining anyone —
   * §7.2 gives that decision to a clinician. Age and UK residence are the
   * exception: objective, and §4.4 lets non-clinical staff apply them.
   */
  const eligibilityAnswered = SCREENING_QUESTIONS.every(
    (q) => typeof eligibilityAnswers[q.key] === 'boolean',
  )
    && typeof eligibilityAnswers.ukResident === 'boolean'
    && dateOfBirth.trim().length > 0;

  const eligibilityClear = eligibilityBlockers.length === 0 && eligibilityFlags.length === 0;

  const canPay =
    Boolean(selectedOption)
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
    && form.firstName.trim().length > 0
    && form.lastName.trim().length > 0
    && form.addressLine1.trim().length > 0
    && form.city.trim().length > 0
    && form.postcode.trim().length > 0
    && (currentUser ? true : password.trim().length >= 8)
    && requiredConsentsAccepted
    && eligibilityAnswered
    && eligibilityClear;

  // Thin header progress bar — fills as required items are completed.
  const progressChecks = [
    Boolean(selectedOption),
    form.firstName.trim().length > 0,
    form.lastName.trim().length > 0,
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email),
    currentUser ? true : password.trim().length >= 8,
    form.addressLine1.trim().length > 0,
    form.city.trim().length > 0,
    form.postcode.trim().length > 0,
    consents.terms,
    consents.privacy,
    consents.healthDataProcessing,
    consents.geneticsContext,
    // Counted so the bar does not read as complete while the section that decides
    // whether Melio should take this person on is still blank.
    eligibilityAnswered,
  ];
  const progress = progressChecks.filter(Boolean).length / progressChecks.length;

  const update = (key: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Stay on the page: reveal the form below and glide DOWN to it.
  const handleChoose = (optionId: EnrollmentOptionId) => {
    setSelectedOptionId(optionId);
    setError(null);
    window.setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canPay || !selectedOption || !checkoutProduct) return;

    setError(null);
    setIsPaying(true);

    try {
      let bookingUserId = currentUser?.uid || null;
      const trimmedEmail = form.email.trim();

      if (!currentUser) {
        try {
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            trimmedEmail,
            password,
          );
          bookingUserId = userCredential.user.uid;

          await setDoc(
            doc(db, 'users', userCredential.user.uid),
            {
              uid: userCredential.user.uid,
              email: userCredential.user.email,
              displayName,
              firstName: form.firstName.trim(),
              lastName: form.lastName.trim(),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          );

          // Acquisition first-touch — non-fatal on failure. See
          // src/services/acquisitionService.ts and
          // docs/markdown/eng/CTO_BRIEF_ACQUISITION_TRACKING.md §4.3.
          try {
            const firstTouch: FirstTouchPayload | null = readFirstTouchForSignup();
            const completeSignup = httpsCallable<
              { firstTouch: FirstTouchPayload | null },
              { ok: boolean; alreadySet?: boolean; channel?: string; campaignId?: string | null }
            >(functions, 'completeSignup');
            await completeSignup({ firstTouch });
          } catch (acqErr) {
            console.warn('completeSignup failed (non-fatal):', acqErr);
          }
        } catch (authErr) {
          if (
            authErr instanceof FirebaseError
            && authErr.code === 'auth/email-already-in-use'
          ) {
            setError(
              'An account already exists for this email. Please log in and then continue your enrollment.',
            );
            return;
          }
          throw authErr;
        }
      }

      if (bookingUserId) {
        await setDoc(
          doc(db, 'users', bookingUserId),
          {
            uid: bookingUserId,
            email: trimmedEmail,
            displayName,
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      }

      /**
       * Eligibility, before any money moves.
       *
       * Runs here rather than as the form is filled in because the callable needs
       * an authenticated caller and the account has only just been created — but
       * still before checkout, which is what §5.1(6) and §6.3 actually require.
       * The verdict is the server's: a browser could compute it faster, but §7.6
       * needs the criterion recorded and §9 audits those decisions against
       * protected characteristics, neither of which survives being done in a tab.
       */
      const screening = await recordEligibilityScreening({
        dateOfBirth,
        ukResident: eligibilityAnswers.ukResident ?? null,
        gpRegistered: eligibilityAnswers.gpRegistered ?? null,
        gpPracticeName: eligibilityAnswers.gpPracticeName,
        ableToTakePartRemotely: eligibilityAnswers.ableToTakePartRemotely ?? null,
        seekingSymptomDiagnosis: eligibilityAnswers.seekingSymptomDiagnosis ?? null,
        needsPrescription: eligibilityAnswers.needsPrescription ?? null,
        eatingDisorder: eligibilityAnswers.eatingDisorder ?? null,
        mentalHealthCrisis: eligibilityAnswers.mentalHealthCrisis ?? null,
        pregnant: eligibilityAnswers.pregnant ?? null,
        urgentConcern: eligibilityAnswers.urgentConcern ?? null,
      });

      if (screening.outcome !== 'accepted') {
        // §6.3 — "not enrolled until the acute problem is resolved", and §7.5:
        // "Any deposit or fee paid is refunded in full." The cleanest way to
        // honour that is not to take the fee. The account exists so a clinician
        // can reach them; nothing has been charged.
        setEligibilityBlockers(screening.blockers || []);
        setEligibilityFlags(screening.flags || []);
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const consentEvents = buildBookPageConsentEvents(consents);
      await recordConsentEvents({
        userId: bookingUserId || undefined,
        events: consentEvents,
      });
      const consentStatusByType = buildConsentSnapshotMap(consentEvents);

      const { addressLine1, addressLine2, city, postcode, ...rest } = form;
      const shippingAddress = { addressLine1, addressLine2, city, postcode };

      if (checkoutPackageId === 'assessment_only') {
        await createConsultationRequest({
          ...rest,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          displayName,
          email: trimmedEmail,
          userId: bookingUserId,
          packageId: checkoutPackageId,
          packageName: checkoutProduct.name,
          shippingAddress,
          consentSnapshot: {
            terms: consentStatusByType.terms || 'declined',
            privacy: consentStatusByType.privacy || 'declined',
            healthDataProcessing: consentStatusByType.health_data_processing || 'declined',
            geneticsContext: consentStatusByType.genetics_context || 'declined',
            geneticDataRetention: consentStatusByType.genetic_data_retention || 'declined',
            version: ONBOARDING_CONSENT_VERSION,
          },
        });
      }

      const createSession = httpsCallable<
        {
          packageId: string;
          email: string;
          name?: string;
          userId?: string | null;
          shippingAddress?: {
            addressLine1: string;
            addressLine2?: string;
            city: string;
            postcode: string;
          };
          successUrl?: string;
          cancelUrl?: string;
        },
        { url?: string; paymentId?: string; packageId?: string; dummy?: boolean; error?: string }
      >(functions, 'createServiceCheckoutSession');

      const base = window.location.origin;
      const cancelSuffix = selectedOption ? `?package=${selectedOption.id}` : '';
      const result = await createSession({
        packageId: checkoutPackageId,
        email: trimmedEmail,
        name: displayName,
        userId: bookingUserId,
        shippingAddress: form.addressLine1
          ? {
            addressLine1: form.addressLine1,
            addressLine2: form.addressLine2,
            city: form.city,
            postcode: form.postcode,
          }
          : undefined,
        successUrl: `${base}${ENROLLMENT_THANK_YOU_PATH}`,
        cancelUrl: `${base}${ENROLLMENT_PATH}${cancelSuffix}`,
      });

      const data = result.data as {
        url?: string;
        paymentId?: string;
        packageId?: string;
        dummy?: boolean;
        error?: string;
      };
      if (data.error) {
        setError(data.error);
        return;
      }

      if (data.dummy && data.paymentId) {
        clearEnrollmentDraft();
        navigate(
          `${ENROLLMENT_THANK_YOU_PATH}?paid=1&paymentId=${data.paymentId}&packageId=${data.packageId || checkoutPackageId}`,
          { state: { email: form.email, paid: true } },
        );
        return;
      }

      if (data.url) {
        window.location.href = data.url;
        return;
      }

      setError('Unexpected response');
    } catch (err: unknown) {
      const msg = err instanceof Error
        ? err.message
        : 'Failed to start payment. Please try again.';
      if (
        msg.toLowerCase().includes('permission')
        || msg.toLowerCase().includes('unavailable')
      ) {
        navigate(ENROLLMENT_THANK_YOU_PATH, {
          state: { email: form.email, paid: false, fallback: true },
        });
        return;
      }
      setError(msg);
    } finally {
      setIsPaying(false);
    }
  };

  // Placed after every hook so the hook order is unconditional.
  if (redirectSignedInMember) {
    return <Navigate to="/testing" replace />;
  }

  return (
    <PublicMarketingLayout
      activePage="home"
      mainClassName="flex-1"
      minimalHeader
      progress={progress}
    >
      <div className="gh-enrol-bg" style={{ color: 'var(--gh-graphite)' }}>
        <main className="mx-auto max-w-5xl px-6 py-16 md:px-10 md:py-24">
          {/* Page heading */}
          <div className="mb-12 text-center">
            <h1
              className="gh-serif"
              style={{ fontSize: 'clamp(2.4rem, 5vw, 3.6rem)', lineHeight: 1.1, margin: 0 }}
            >
              Apply for membership.
            </h1>
            <p
              className="gh-serif"
              style={{
                fontStyle: 'italic',
                fontSize: '1.125rem',
                color: 'var(--gh-ash)',
                marginTop: 14,
                maxWidth: 560,
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              One membership, with a one-time intro assessment to begin. Choose to
              pay monthly or settle the year in a single payment.
            </p>
          </div>

          {/* Selection — single membership, stacked pay-by cards (Maison style) */}
          <div className="mx-auto" style={{ maxWidth: 600 }}>
            <p
              className="gh-eyebrow"
              style={{ textAlign: 'center', marginBottom: 18, color: 'var(--gh-oxblood)' }}
            >
              Please select how you&rsquo;d like to pay
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {ENROLLMENT_OPTIONS.map((option) => {
                const isSelected = selectedOptionId === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleChoose(option.id)}
                    aria-pressed={isSelected}
                    className="gh-plan-card"
                    data-selected={isSelected ? 'true' : 'false'}
                  >
                    {option.badge && <span className="gh-plan-card__badge">{option.badge}</span>}
                    <div className="gh-plan-card__head">
                      <span className="gh-plan-card__label">{option.label}</span>
                      <span className="gh-plan-card__price">{option.priceSummary}</span>
                    </div>
                    <p className="gh-plan-card__cadence">{option.cadence}</p>
                    <p className="gh-plan-card__meta">{option.initiationNote}</p>
                  </button>
                );
              })}
            </div>

            <p
              className="gh-serif"
              style={{
                fontStyle: 'italic',
                fontSize: '0.9rem',
                color: 'var(--gh-ash)',
                textAlign: 'center',
                marginTop: 22,
              }}
            >
              Membership is a 12-month commitment. The intro assessment is your
              one-time initiation and begins your clinical record.
            </p>
          </div>

          {/* Form — revealed once a plan is chosen; the page glides down to it */}
          {selectedOption && (
            <div className="gh-enrol-form-block" ref={formRef} style={{ marginTop: 56 }}>
              <form onSubmit={handlePay} style={{ display: 'block', maxWidth: 640, margin: '0 auto' }}>
                {/* Section: Your details */}
                <section className="mb-12" style={{ position: 'relative' }}>
                  <EnrollVignette src="vignette-kitchen.png" align="right" />
                  <p className="gh-eyebrow-ash mb-2">Section I</p>
                  <h2
                    className="gh-serif"
                    style={{ fontSize: '2rem', margin: 0, marginBottom: 28 }}
                  >
                    Your details
                  </h2>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      columnGap: 32,
                      rowGap: 26,
                    }}
                  >
                    <div>
                      <label className="gh-input-label" htmlFor="firstName">
                        First name
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        className="gh-input"
                        value={form.firstName}
                        onChange={(e) => update('firstName', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="gh-input-label" htmlFor="lastName">
                        Last name
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        className="gh-input"
                        value={form.lastName}
                        onChange={(e) => update('lastName', e.target.value)}
                        required
                      />
                    </div>

                    <div style={{ gridColumn: '1 / -1' }}>
                      <label className="gh-input-label" htmlFor="email">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        className="gh-input"
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        required
                        disabled={Boolean(currentUser)}
                      />
                      {currentUser && (
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'var(--gh-ash)',
                            marginTop: 6,
                          }}
                        >
                          Purchase will use your signed-in account email.
                        </p>
                      )}
                    </div>

                    {!currentUser && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label className="gh-input-label" htmlFor="password">
                          Password
                        </label>
                        <input
                          id="password"
                          type="password"
                          className="gh-input"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={8}
                        />
                        <p
                          style={{
                            fontSize: '0.75rem',
                            color: 'var(--gh-ash)',
                            marginTop: 6,
                          }}
                        >
                          Your Melio account will be created as part of enrolment.
                        </p>
                      </div>
                    )}

                    <div style={{ gridColumn: '1 / -1' }}>
                      <label className="gh-input-label" htmlFor="phone">
                        Phone
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        className="gh-input"
                        value={form.phone}
                        onChange={(e) => update('phone', e.target.value)}
                      />
                    </div>
                  </div>
                </section>

                {/* Section: Where to send your kit */}
                <section className="mb-12" style={{ position: 'relative' }}>
                  <EnrollVignette src="vignette-coast.png" align="left" />
                  <p className="gh-eyebrow-ash mb-2">Section II</p>
                  <h2
                    className="gh-serif"
                    style={{ fontSize: '2rem', margin: 0, marginBottom: 8 }}
                  >
                    Where to send your kit
                  </h2>
                  <p
                    className="gh-serif"
                    style={{
                      fontStyle: 'italic',
                      color: 'var(--gh-ash)',
                      fontSize: '1rem',
                      marginBottom: 28,
                    }}
                  >
                    Genetics kit and any pathology supplies will be posted here.
                  </p>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      rowGap: 26,
                    }}
                  >
                    <div>
                      <label className="gh-input-label" htmlFor="addressLine1">
                        Address line 1
                      </label>
                      <input
                        id="addressLine1"
                        type="text"
                        className="gh-input"
                        value={form.addressLine1}
                        onChange={(e) => update('addressLine1', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="gh-input-label" htmlFor="addressLine2">
                        Address line 2
                      </label>
                      <input
                        id="addressLine2"
                        type="text"
                        className="gh-input"
                        value={form.addressLine2}
                        onChange={(e) => update('addressLine2', e.target.value)}
                      />
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        columnGap: 32,
                      }}
                    >
                      <div>
                        <label className="gh-input-label" htmlFor="city">
                          City
                        </label>
                        <input
                          id="city"
                          type="text"
                          className="gh-input"
                          value={form.city}
                          onChange={(e) => update('city', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <label className="gh-input-label" htmlFor="postcode">
                          Postcode
                        </label>
                        <input
                          id="postcode"
                          type="text"
                          className="gh-input"
                          value={form.postcode}
                          onChange={(e) => update('postcode', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="gh-input-label" htmlFor="message">
                        A note for your clinician (optional)
                      </label>
                      <textarea
                        id="message"
                        className="gh-input"
                        value={form.message}
                        onChange={(e) => update('message', e.target.value)}
                        rows={3}
                      />
                    </div>
                  </div>
                </section>

                {/* Section: Eligibility — Inclusion and Exclusion Criteria §5, §6.
                    Before consent and before payment: §5.1(6) requires the scope
                    to be explained "before paying", and there is no point taking
                    someone's consent to a service they should not be joining. */}
                <section className="mb-12" style={{ position: 'relative' }}>
                  <p className="gh-eyebrow-ash mb-2">Section III</p>
                  <h2
                    className="gh-serif"
                    style={{ fontSize: '2rem', margin: 0, marginBottom: 8 }}
                  >
                    Is Melio right for you?
                  </h2>
                  <div style={{ marginTop: 24 }}>
                    <EligibilityScreening
                      dateOfBirth={dateOfBirth}
                      onDateOfBirth={setDateOfBirth}
                      answers={eligibilityAnswers}
                      onAnswers={setEligibilityAnswers}
                      blockers={eligibilityBlockers}
                      flags={eligibilityFlags}
                    />
                  </div>
                </section>

                <hr className="gh-rule" style={{ margin: '0 0 36px 0' }} />

                {/* Section: Consents */}
                <section className="mb-12" style={{ position: 'relative' }}>
                  <EnrollVignette src="vignette-cycling.png" align="right" />
                  <p className="gh-eyebrow-ash mb-2">Section IV</p>
                  <h2
                    className="gh-serif"
                    style={{ fontSize: '2rem', margin: 0, marginBottom: 8 }}
                  >
                    Consent &amp; agreement
                  </h2>
                  <p
                    className="gh-serif"
                    style={{
                      fontStyle: 'italic',
                      color: 'var(--gh-ash)',
                      fontSize: '1rem',
                      marginBottom: 28,
                    }}
                  >
                    Required items first, then any optional permissions.
                  </p>

                  <div role="group" aria-labelledby="consents-required">
                    <p
                      id="consents-required"
                      className="gh-eyebrow-ash"
                      style={{ marginBottom: 8, color: 'var(--gh-oxblood)' }}
                    >
                      Required
                    </p>

                    <ConsentRow
                      checked={consents.terms}
                      onChange={(checked) =>
                        setConsents((c) => ({ ...c, terms: checked }))
                      }
                      requiredKind
                    >
                      I accept Melio&rsquo;s{' '}
                      <Link to="/terms" className="gh-link">
                        Terms
                      </Link>
                      .
                    </ConsentRow>

                    <ConsentRow
                      checked={consents.privacy}
                      onChange={(checked) =>
                        setConsents((c) => ({ ...c, privacy: checked }))
                      }
                      requiredKind
                    >
                      I acknowledge Melio&rsquo;s{' '}
                      <Link to="/privacy" className="gh-link">
                        Privacy Policy
                      </Link>
                      .
                    </ConsentRow>

                    <ConsentRow
                      checked={consents.healthDataProcessing}
                      onChange={(checked) =>
                        setConsents((c) => ({ ...c, healthDataProcessing: checked }))
                      }
                      requiredKind
                    >
                      I consent to Melio processing my health data to coordinate
                      onboarding, clinician review, logistics, and follow-up.
                    </ConsentRow>

                    <ConsentRow
                      checked={consents.geneticsContext}
                      onChange={(checked) =>
                        setConsents((c) => ({ ...c, geneticsContext: checked }))
                      }
                      requiredKind
                    >
                      I understand Melio&rsquo;s opening genetics layer is a context-only
                      SNP / preventive test, not a definitive diagnostic genetics service.
                      Where needed, Melio may recommend confirmatory testing through an
                      accredited clinical laboratory.
                    </ConsentRow>
                  </div>

                  {/*
                    Consent §4.1(4) asks for retention of raw genetic data as its
                    own, explicit confirmation — separate from agreeing to the test
                    above. §11.3 then lets a member withdraw it on its own, and the
                    data is deleted without the membership ending, which is what
                    makes it genuinely optional rather than optional in name.

                    The two consents that used to sit here — research use and
                    commercial data use — are gone. §4.4: "Melio does not seek
                    consent for any purpose beyond those listed in Section 4.1",
                    and neither is among the six.
                  */}
                  <div role="group" aria-labelledby="consents-optional" style={{ marginTop: 32 }}>
                    <p
                      id="consents-optional"
                      className="gh-eyebrow-ash"
                      style={{ marginBottom: 8 }}
                    >
                      Optional
                    </p>

                    <ConsentRow
                      checked={consents.geneticDataRetention}
                      onChange={(checked) =>
                        setConsents((c) => ({ ...c, geneticDataRetention: checked }))
                      }
                    >
                      Melio may keep my raw genetic data so it can be looked at again as
                      medical knowledge develops, without asking me for a new sample. I can
                      withdraw this at any time from my account, and the raw data is then
                      deleted — my membership and the findings my clinician has already
                      reviewed are unaffected.
                    </ConsentRow>
                  </div>
                </section>

                <hr className="gh-rule" style={{ margin: '0 0 36px 0' }} />

                {/* Submit */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                  {error && (
                    <p
                      style={{
                        fontSize: '0.875rem',
                        color: 'var(--gh-oxblood)',
                        textAlign: 'center',
                      }}
                    >
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={!canPay || isPaying}
                    className="gh-btn-primary"
                    style={{
                      minWidth: 240,
                      padding: '16px 32px',
                      fontSize: '0.9375rem',
                      letterSpacing: '0.05em',
                      opacity: !canPay || isPaying ? 0.55 : 1,
                      cursor: !canPay || isPaying ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isPaying ? 'Redirecting to purchase…' : `Continue to ${selectedOption.label}`}
                  </button>
                  <p
                    className="gh-serif"
                    style={{
                      fontStyle: 'italic',
                      fontSize: '0.875rem',
                      color: 'var(--gh-ash)',
                      textAlign: 'center',
                      maxWidth: 440,
                      margin: 0,
                    }}
                  >
                    {selectedOption.detailNote
                      || 'Payment is processed securely by Stripe. You can cancel anytime before clinician review begins.'}
                  </p>
                </div>
              </form>
            </div>
          )}
        </main>
      </div>
    </PublicMarketingLayout>
  );
};

interface ConsentRowProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  requiredKind?: boolean;
  children: React.ReactNode;
}

const ConsentRow: React.FC<ConsentRowProps> = ({ checked, onChange, requiredKind, children }) => (
  <label className="gh-consent-row">
    <span className="gh-consent-row__check" data-checked={checked} aria-hidden="true">
      <Checkmark />
    </span>
    <span className="gh-consent-row__label">
      <span className={`gh-consent-row__kind ${requiredKind ? 'is-required' : ''}`}>
        {requiredKind ? 'Required' : 'Optional'}
      </span>
      {children}
    </span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
    />
  </label>
);

export default EnrollmentPage;
